// src/components/mdx/client/ChartComponent.client.tsx

/**
 * Chart Component
 * 
 * A reusable chart component that supports various chart types and configurations.
 * This component provides a consistent interface for rendering different chart types
 * while maintaining type safety and performance optimizations.
 * 
 * OPTIMIZATION NOTE:
 * This component uses centralized imports from '~/lib/charts' instead of direct imports
 * from 'recharts'. This approach provides several bundle optimization benefits:
 * 
 * 1. Shared module chunking - All chart components share the same recharts code chunks
 * 2. Enhanced tree-shaking - More efficient dead code elimination across the codebase
 * 3. Import cost amortization - The fixed overhead cost of importing recharts is shared
 * 4. Deduplication - Better handling of nested dependencies within recharts
 * 5. Consistent code splitting - Creates cleaner boundaries for code splitting
 * 
 * As our application contains multiple visualizations, this approach significantly
 * reduces the overall bundle size compared to direct imports from 'recharts'.
 */

'use client'

import { type ReactNode, type ReactElement, type FC, type ComponentType, forwardRef, useMemo } from 'react'
import { motion } from "motion/react"
import { cn } from '~/lib/utils'
import { createSafeMDXComponent } from '~/components/mdx/utils/component-utils.client'
import {
  Line,
  LineChart,
  Bar,
  BarChart,
  Pie,
  PieChart,
  Scatter,
  ScatterChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ZAxis,
} from '~/lib/charts/client'
import {  
  type TooltipProps,
  type DataKey
} from '~/lib/charts'
import { safeString } from '~/lib/utils/formatters'

/**
 * Defines the available chart types
 * @typedef {('line'|'bar'|'pie'|'scatter')} ChartType
 */
export type ChartType = 'line' | 'bar' | 'pie' | 'scatter'

/**
 * Represents a single data point in a chart series
 * @interface ChartDataPoint
 * @typedef {Object} ChartDataPoint
 * @property {string} label - The label for this data point (used for x-axis in most charts)
 * @property {number} value - The numeric value for this data point
 * @property {Object} [key: string] - Optional additional values for multi-dimensional charts
 */
export interface ChartDataPoint {
  /** The label for this data point (used for x-axis in most charts) */
  label: string
  /** The numeric value for this data point */
  value: number
  /** Optional additional values for multi-dimensional charts */
  [key: string]: string | number | null | undefined
}

/**
 * Represents a series of data in a chart
 * @interface ChartSeries
 * @typedef {Object} ChartSeries
 * @property {string} name - The name of this data series
 * @property {ChartDataPoint[]} data - The array of data points in this series
 * @property {string} [color] - Optional color for this series
 * @property {string} [dataKey] - Optional key to use for accessing data values (defaults to 'value')
 */
export interface ChartSeries {
  /** The name of this data series */
  name: string
  /** The array of data points in this series */
  data: ChartDataPoint[]
  /** Optional color for this series */
  color?: string
  /** Optional key to use for accessing data values (defaults to 'value') */
  dataKey?: string
}

/**
 * Props for the ChartContainer component
 * @interface ChartContainerProps
 * @extends React.HTMLAttributes<HTMLDivElement>
 * @property {object} [config] - Configuration for chart series (Record<string, {label: string, color: string}>)
 */
export interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Configuration for chart series */
  config?: Record<string, {
    label: string
    color: string
  }>
}

/**
 * A container component for charts with forwarded refs
 * @component
 * @param {ChartContainerProps} props - The component props
 * @param {ReactNode} props.children - The child elements to render inside the container
 * @param {string} [props.className] - Additional CSS classes for the container
 * @returns {ReactElement} The rendered chart container component
 */
const ChartContainer = forwardRef<HTMLDivElement, ChartContainerProps>(
  ({ className, ...props }, ref) => {
    return (
      <div className={cn("chart-container", className)} {...props} ref={ref}>
        {props.children}
      </div>
    )
  }
)
ChartContainer.displayName = "ChartContainer"

/**
 * Base props for all chart components
 * @interface ChartProps
 * @typedef {Object} ChartProps
 * @property {ChartType} type - The type of chart to render
 * @property {ChartSeries[]} data - The data series to display in the chart
 * @property {string} [title] - Optional title for the chart
 * @property {string} [description] - Optional description for the chart
 * @property {string} [className] - Additional CSS classes for the chart
 */
export interface ChartProps {
  /** The type of chart to render */
  type: ChartType
  /** The data series to display in the chart */
  data: ChartSeries[]
  /** Optional title for the chart */
  title?: string
  /** Optional description for the chart */
  description?: string
  /** Optional additional CSS classes */
  className?: string
}

/**
 * Props for the enhanced chart component with additional configuration options
 * @interface EnhancedChartProps
 * @extends ChartProps
 * @property {string} [subtitle] - Optional subtitle for the chart
 * @property {ReactNode} [footer] - Optional footer content
 * @property {boolean} [legend] - Whether to show the legend (default: true)
 * @property {boolean} [grid] - Whether to show grid lines (default: true)
 * @property {boolean} [interactive] - Whether the chart should be interactive (default: true)
 * @property {number} [height] - Height of the chart in pixels (default: 320)
 * @property {string} [xAxisKey] - Key to use for x-axis values (default: 'name')
 * @property {string} [yAxisKey] - Key to use for y-axis values (default: 'value')
 * @property {string} [xAxisLabel] - Optional label for the x-axis
 * @property {string} [yAxisLabel] - Optional label for the y-axis
 * @property {React.ComponentType<CustomTooltipProps>} [customTooltip] - Optional custom tooltip component
 * @property {object} [margin] - Optional margin for the chart (default: { top: 20, right: 30, left: 20, bottom: 30 })
 */
export interface EnhancedChartProps extends ChartProps {
  /** Optional subtitle for the chart */
  subtitle?: string
  /** Optional footer content */
  footer?: ReactNode
  /** Whether to show the legend (default: true) */
  legend?: boolean
  /** Whether to show grid lines (default: true) */
  grid?: boolean
  /** Whether the chart should be interactive (default: true) */
  interactive?: boolean
  /** Height of the chart in pixels (default: 320) */
  height?: number
  /** Key to use for x-axis values (default: 'name') */
  xAxisKey?: string
  /** Key to use for y-axis values (default: 'value') */
  yAxisKey?: string
  /** Optional label for the x-axis */
  xAxisLabel?: string
  /** Optional label for the y-axis */
  yAxisLabel?: string
  /** Optional custom tooltip component */
  customTooltip?: React.ComponentType<CustomTooltipProps>
  /** Optional margin for the chart */
  margin?: { top: number; right: number; bottom: number; left: number }
}

/**
 * Type for recharts tooltip payload item
 * @interface TooltipPayloadItem
 * @typedef {Object} TooltipPayloadItem
 * @property {number} value - The value of the data point
 * @property {string} name - The name of the data series
 * @property {string} [dataKey] - The data key used for this item
 * @property {string} [color] - Optional color for this data point
 * @property {Object} [key: string] - Additional properties
 */
export interface TooltipPayloadItem {
  /** The value of the data point */
  value: number
  /** The name of the data series */
  name: string
  /** The data key used for this item */
  dataKey: string
  /** Optional color for this data point */
  color?: string
  /** Additional properties */
  [key: string]: string | number | undefined
}

/**
 * Props for the custom tooltip component
 * Updated for Recharts 3.0: includes required accessibilityLayer prop
 * @interface CustomTooltipProps
 * @typedef {Object} CustomTooltipProps
 * @property {boolean} [active] - Whether the tooltip is active
 * @property {TooltipPayloadItem[]} [payload] - The payload data for the tooltip
 * @property {string} [label] - The label for the tooltip
 * @property {boolean} [accessibilityLayer] - Whether accessibility layer is enabled (Recharts 3.0+)
 */
export interface CustomTooltipProps {
  /** Whether the tooltip is active */
  active?: boolean
  /** The payload data for the tooltip */
  payload?: TooltipPayloadItem[]
  /** The label for the tooltip */
  label?: string
  /** Whether accessibility layer is enabled (Recharts 3.0+) - required by Recharts 3.0 */
  accessibilityLayer: boolean
}

/**
 * Default custom tooltip component
 * @component
 * @param {CustomTooltipProps} props - The component props
 * @returns {ReactElement} The rendered tooltip component
 */
const DefaultTooltip: FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-gray-800/90 backdrop-blur-sm border border-gray-700 rounded-lg shadow-lg p-4">
        <p className="text-gray-300 font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={`tooltip-${index}`} className="text-sm text-gray-400">
            {entry.name}: <span className="font-medium" style={{ color: entry.color }}>{entry.value}</span>
          </p>
        ))}
      </div>
    )
  }
  return null
}

/**
 * Type for transformed chart data
 * @typedef {Record<string, string | number>[]} TransformedChartData
 */
type TransformedChartData = Record<string, string | number>[]

/**
 * Simple chart component with basic functionality
 * @component
 * @param {ChartProps} props - The component props
 * @returns {ReactElement} The rendered chart component
 */
export function ChartComponent({
  type,
  data,
  title,
  description,
  className
}: ChartProps): ReactElement {
  // Transform data for recharts
  const chartData = useMemo((): TransformedChartData => {
    if (!data.length || !data[0]?.data.length) {
      return []
    }
    
    return data[0].data.map((point, i) => {
      const result: Record<string, string | number> = { name: point.label }
      data.forEach(series => {
        if (i < series.data.length) {
          result[series.name] = series.data[i]?.value ?? 0
        }
      })
      return result
    })
  }, [data])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={cn(
        'my-8 overflow-hidden rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900',
        className
      )}
    >
      {title && (
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
      )}
      {description && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {description}
        </p>
      )}
      <div className="mt-6 h-64">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart(type, chartData, data)}
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}

/**
 * Enhanced chart component with more features and interactivity
 * @component
 * @param {EnhancedChartProps} props - The component props
 * @returns {ReactElement} The rendered enhanced chart component
 */
export function EnhancedChartComponent({
  type,
  data,
  title,
  subtitle,
  description,
  footer,
  legend = true,
  grid = true,
  interactive = true,
  height = 320,
  className,
  xAxisKey = 'name',
  yAxisKey = 'value',
  xAxisLabel,
  yAxisLabel,
  customTooltip,
  margin = { top: 20, right: 30, left: 20, bottom: 30 }
}: EnhancedChartProps): ReactElement {
  // Transform data for recharts
  const chartData = useMemo((): TransformedChartData => {
    if (!data.length || !data[0]?.data.length) {
      return []
    }
    
    return data[0].data.map((point, i) => {
      const result: Record<string, string | number> = { name: point.label }
      data.forEach(series => {
        if (i < series.data.length) {
          result[series.name] = series.data[i]?.value ?? 0
        }
      })
      return result
    })
  }, [data])

  const TooltipComponent = customTooltip ?? DefaultTooltip

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={cn(
        'my-8 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-900',
        className
      )}
    >
      <div className="p-6">
        {/* Header */}
        {(title ?? subtitle) && (
          <div className="space-y-1">
            {title && (
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* Description */}
        {description && (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {description}
          </p>
        )}

        {/* Chart Area */}
        <div 
          className={cn(
            "mt-6 w-full",
            interactive && "cursor-pointer"
          )}
          style={{ height }}
        >
          <ResponsiveContainer width="100%" height="100%">
            {renderEnhancedChart(
              type, 
              chartData, 
              data, 
              {
                grid,
                legend,
                xAxisKey,
                yAxisKey,
                xAxisLabel,
                yAxisLabel,
                TooltipComponent,
                margin
              }
            )}
          </ResponsiveContainer>
        </div>

        {/* Custom Legend (in addition to recharts legend) */}
        {legend && (
          <div className="mt-6 flex flex-wrap gap-4">
            {data.map((series, index) => (
              <div key={`legend-${index}`} className="flex items-center space-x-2">
                <div 
                  className="h-3 w-3 rounded-full" 
                  style={{ backgroundColor: series.color ?? `hsl(${index * 60}, 70%, 50%)` }}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {series.name}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {footer && (
          <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800">
            {footer}
          </div>
        )}
      </div>
    </motion.div>
  )
}

/**
 * Renders the appropriate chart based on the specified type
 * 
 * @param {ChartType} type - The type of chart to render
 * @param {TransformedChartData} chartData - The transformed data for the chart
 * @param {ChartSeries[]} seriesData - The original series data
 * @returns {ReactElement} The rendered chart component
 */
function renderChart(
  type: ChartType, 
  chartData: TransformedChartData, 
  seriesData: ChartSeries[]
): ReactElement {
  if (chartData.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500">No data available</p>
      </div>
    )
  }

  switch (type) {
    case 'line':
      return (
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip content={<DefaultTooltip accessibilityLayer={false} />} />
          <Legend />
          {seriesData.map((series, index) => (
            <Line
              key={`line-${index}`}
              type="monotone"
              dataKey={series.name}
              stroke={series.color ?? `hsl(${index * 60}, 70%, 50%)`}
              activeDot={{ r: 8 }}
            />
          ))}
        </LineChart>
      )
    
    case 'bar':
      return (
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip content={<DefaultTooltip accessibilityLayer={false} />} />
          <Legend />
          {seriesData.map((series, index) => (
            <Bar
              key={`bar-${index}`}
              dataKey={series.name}
              fill={series.color ?? `hsl(${index * 60}, 70%, 50%)`}
            />
          ))}
        </BarChart>
      )
    
    case 'pie':
      return (
        <PieChart>
          <Pie
            data={chartData}
            dataKey={seriesData[0]?.name ?? "value"}
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={80}
            label
          >
            {chartData.map((_entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={seriesData[0]?.color ?? `hsl(${index * 36}, 70%, 50%)`} 
              />
            ))}
          </Pie>
          <Tooltip content={<DefaultTooltip accessibilityLayer={false} />} />
          <Legend />
        </PieChart>
      )
    
    case 'scatter':
      return (
        <ScatterChart>
          <CartesianGrid />
          <XAxis dataKey="name" type="category" />
          <YAxis dataKey={seriesData[0]?.name ?? "value"} />
          <ZAxis range={[100, 200]} />
          <Tooltip content={<DefaultTooltip accessibilityLayer={false} />} />
          <Legend />
          {seriesData.map((series, index) => (
            <Scatter
              key={`scatter-${index}`}
              name={series.name}
              data={chartData}
              fill={series.color ?? `hsl(${index * 60}, 70%, 50%)`}
            />
          ))}
        </ScatterChart>
      )
    
    default:
      return (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-gray-500">Unsupported chart type: {type}</p>
        </div>
      )
  }
}

/**
 * Interface for enhanced chart rendering options
 * @interface EnhancedChartOptions
 * @typedef {Object} EnhancedChartOptions
 * @property {boolean} grid - Whether to show grid lines
 * @property {boolean} legend - Whether to show the legend
 * @property {string} xAxisKey - Key to use for x-axis values
 * @property {string} yAxisKey - Key to use for y-axis values
 * @property {string} [xAxisLabel] - Optional label for the x-axis
 * @property {string} [yAxisLabel] - Optional label for the y-axis
 */
interface EnhancedChartOptions {
  /** Whether to show grid lines */
  grid: boolean
  /** Whether to show the legend */
  legend: boolean
  /** Key to use for x-axis values */
  xAxisKey: string
  /** Key to use for y-axis values */
  yAxisKey: string
  /** Optional label for the x-axis */
  xAxisLabel?: string
  /** Optional label for the y-axis */
  yAxisLabel?: string
  /** Component to use for tooltips */
  TooltipComponent: ComponentType<CustomTooltipProps>
  /** Margin for the chart */
  margin: { top: number; right: number; bottom: number; left: number }
}

/**
 * Custom tooltip wrapper that adapts recharts tooltip data to our format
 * 
 * @param {ComponentType<CustomTooltipProps>} Component - The tooltip component to wrap
 * @returns {ComponentType<TooltipProps<Record<string, unknown>>>} The wrapped tooltip component
 */
function createTooltipWrapper(Component: ComponentType<CustomTooltipProps>): ComponentType<TooltipProps<Record<string, unknown>>> {
  return function TooltipWrapper(props: TooltipProps<Record<string, unknown>>): ReactElement {
    const { active, payload, label, accessibilityLayer = false } = props
    
    // Create properly typed payload for our custom tooltip component
    const typedPayload: TooltipPayloadItem[] = []
    
    if (payload && payload.length > 0) {
      for (const item of payload) {
        if (item?.value !== undefined) {
          // Extract properties safely with type checking using safeString utility
          // This prevents the '[object Object]' stringification issue and provides meaningful fallbacks
          const payloadItem: TooltipPayloadItem = {
            value: typeof item.value === 'number' ? item.value : 0,
            // Use safeString to safely convert payload properties to strings
            // Fallback chain: name -> label -> empty string for missing values
            name: safeString(item.payload?.name) || safeString(item.payload?.label) || '',
            // Convert dataKey safely to string, with empty string fallback
            dataKey: safeString(item.payload?.dataKey) || '',
            // Ensure color is a valid string or undefined
            color: typeof item.color === 'string' ? item.color : undefined
          }
          typedPayload.push(payloadItem)
        }
      }
    }
    
    return <Component active={active} payload={typedPayload} label={label} accessibilityLayer={accessibilityLayer} />
  }
}

/**
 * Renders an enhanced chart with additional configuration options
 * 
 * @param {ChartType} type - The type of chart to render
 * @param {TransformedChartData} chartData - The transformed data for the chart
 * @param {ChartSeries[]} seriesData - The original series data
 * @param {EnhancedChartOptions} options - Additional configuration options
 * @returns {ReactElement} The rendered chart component
 */
function renderEnhancedChart(
  type: ChartType, 
  chartData: TransformedChartData, 
  seriesData: ChartSeries[],
  options: EnhancedChartOptions
): ReactElement {
  const { 
    grid, 
    legend, 
    xAxisKey, 
    yAxisKey, 
    xAxisLabel, 
    yAxisLabel, 
    TooltipComponent, 
    margin 
  } = options

  if (chartData.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500">No data available</p>
      </div>
    )
  }

  // Create a properly typed tooltip component for recharts
  const TooltipContent = createTooltipWrapper(TooltipComponent)

  switch (type) {
    case 'line':
      return (
        <LineChart data={chartData} margin={margin}>
          {grid && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis 
            dataKey={xAxisKey as DataKey<Record<string, string | number>>} 
            label={xAxisLabel ? { value: xAxisLabel, position: 'bottom', offset: -10 } : undefined} 
          />
          <YAxis 
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'left', offset: -5 } : undefined} 
          />
          <Tooltip content={<TooltipContent accessibilityLayer={false} />} />
          {legend && <Legend />}
          {seriesData.map((series, index) => (
            <Line
              key={`line-${index}`}
              type="monotone"
              dataKey={series.name as DataKey<Record<string, string | number>>}
              stroke={series.color ?? `hsl(${index * 60}, 70%, 50%)`}
              activeDot={{ r: 8 }}
            />
          ))}
        </LineChart>
      )
    
    case 'bar':
      return (
        <BarChart data={chartData} margin={margin}>
          {grid && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis 
            dataKey={xAxisKey as DataKey<Record<string, string | number>>} 
            label={xAxisLabel ? { value: xAxisLabel, position: 'bottom', offset: -10 } : undefined} 
          />
          <YAxis 
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'left', offset: -5 } : undefined} 
          />
          <Tooltip content={<TooltipContent accessibilityLayer={false} />} />
          {legend && <Legend />}
          {seriesData.map((series, index) => (
            <Bar
              key={`bar-${index}`}
              dataKey={series.name as DataKey<Record<string, string | number>>}
              fill={series.color ?? `hsl(${index * 60}, 70%, 50%)`}
            />
          ))}
        </BarChart>
      )
    
    case 'pie':
      return (
        <PieChart margin={margin}>
          <Pie
            data={chartData}
            dataKey={(seriesData[0]?.name ?? "value") as DataKey<Record<string, string | number>>}
            nameKey={xAxisKey as DataKey<Record<string, string | number>>}
            cx="50%"
            cy="50%"
            outerRadius={80}
            label
          >
            {chartData.map((_entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={seriesData[0]?.color ?? `hsl(${index * 36}, 70%, 50%)`} 
              />
            ))}
          </Pie>
          <Tooltip content={<TooltipContent accessibilityLayer={false} />} />
          {legend && <Legend />}
        </PieChart>
      )
    
    case 'scatter':
      return (
        <ScatterChart margin={margin}>
          {grid && <CartesianGrid />}
          <XAxis 
            dataKey={xAxisKey as DataKey<Record<string, string | number>>} 
            type="category" 
            label={xAxisLabel ? { value: xAxisLabel, position: 'bottom', offset: -10 } : undefined} 
          />
          <YAxis 
            dataKey={(yAxisKey ?? seriesData[0]?.name ?? "value") as DataKey<Record<string, string | number>>} 
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'left', offset: -5 } : undefined} 
          />
          <ZAxis range={[100, 200]} />
          <Tooltip content={<TooltipContent accessibilityLayer={false} />} cursor={{ strokeDasharray: '3 3' }} />
          {legend && <Legend />}
          {seriesData.map((series, index) => (
            <Scatter
              key={`scatter-${index}`}
              name={series.name}
              data={chartData}
              fill={series.color ?? `hsl(${index * 60}, 70%, 50%)`}
            />
          ))}
        </ScatterChart>
      )
    
    default:
      return (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-gray-500">Unsupported chart type: {type}</p>
        </div>
      )
  }
}

// ===== SAFE EXPORTS FOR SERVER COMPONENTS =====

/**
 * Safe version of ChartComponent with error boundaries and loading states.
 * This is the version that should be imported by server components.
 */
export const SafeChartComponent = createSafeMDXComponent(
  () => Promise.resolve({ default: ChartComponent }),
  {
    componentName: 'ChartComponent',
    category: 'chart' as const,
    ssr: false,
    isHierarchical: false,
    preserveChildrenOnError: false,
    disableDefaultWrapper: true // Already wrapped by the component itself
  }
)

/**
 * Safe version of EnhancedChartComponent with error boundaries and loading states.
 * This is the version that should be imported by server components.
 */
export const SafeEnhancedChartComponent = createSafeMDXComponent(
  () => Promise.resolve({ default: EnhancedChartComponent }),
  {
    componentName: 'EnhancedChartComponent',
    category: 'chart' as const,
    ssr: false,
    isHierarchical: false,
    preserveChildrenOnError: false,
    disableDefaultWrapper: true // Already wrapped by the component itself
  }
)
